import * as React from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";
import { useReducedMotion } from "../../hooks/use-reduced-motion";

/**
 * CitationHighlighter — generic visual mark applied to the zone of a
 * source that produced a citation `[n]`.
 *
 * Visual contract:
 *   - orange-soft background (`bg-ando-orange-soft`)
 *   - 2px left border accent (`border-l-2 border-ando-orange`)
 *   - `pulse` animation 1.5s on mount, then static
 *   - `prefers-reduced-motion: reduce` → no pulse, only static highlight
 *
 * Designed to wrap *anything*: a PDF page block, an email paragraph,
 * a calendar event row, a table row, a transaction card. Viewers in
 * `viewers/*` import this rather than re-implementing the highlight
 * primitive — DRY across the 5 (and growing) renderers.
 */
// HTML drag/animation/transition events overlap with framer-motion handlers.
// We drop them at the type level — consumers should rely on framer-motion's
// drag API (out of scope here) or compose a non-animated wrapper.
type MotionSafeDivProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  | "onDrag" | "onDragStart" | "onDragEnd" | "onDragOver" | "onDragEnter"
  | "onDragLeave" | "onDragExit"
  | "onAnimationStart" | "onAnimationEnd" | "onAnimationIteration"
  | "onTransitionEnd"
>;

export interface CitationHighlighterProps extends MotionSafeDivProps {
  /** Render the highlight chrome? `false` = pass-through wrapper. */
  active?: boolean;
  /** Disable the entry pulse even when motion is allowed. */
  staticOnly?: boolean;
}

export const CitationHighlighter = React.forwardRef<
  HTMLDivElement,
  CitationHighlighterProps
>(
  (
    {
      active = true,
      staticOnly = false,
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const reduceMotion = useReducedMotion();
    const shouldPulse = active && !staticOnly && !reduceMotion;

    if (!active) {
      // Pass-through — no chrome, no animation.
      return (
        <div ref={ref} className={className} {...rest}>
          {children}
        </div>
      );
    }

    return (
      <motion.div
        ref={ref}
        initial={shouldPulse ? { backgroundColor: "rgba(255, 122, 26, 0.28)" } : false}
        animate={
          shouldPulse
            ? {
                backgroundColor: [
                  "rgba(255, 122, 26, 0.28)",
                  "rgba(255, 122, 26, 0.12)",
                  "rgba(255, 122, 26, 0.18)",
                ],
              }
            : false
        }
        transition={{ duration: 1.5, ease: "easeOut" }}
        className={cn(
          "relative rounded-md border-ando-orange border-l-2 bg-ando-orange-soft/60 px-2 py-1",
          "shadow-[inset_0_0_0_1px_rgba(255,122,26,0.18)]",
          className,
        )}
        data-ando-citation-highlight="true"
        {...rest}
      >
        {children}
      </motion.div>
    );
  },
);
CitationHighlighter.displayName = "CitationHighlighter";
